import {System} from './../../../shared-components/System';

import {forkJoin as observableForkJoin, Observable, Subject} from 'rxjs';
import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {YearService} from '../../../shared-components/year/year.service';
import {ClassroomSummary} from '../../classroom-summary/models/classroom-summary.model';
import {V10Assignment} from './models/assignment.model';
import {Year} from '../../../shared-components/year/year.model';
import {Student} from './models/student.model';
import {GradebookPreferences} from '../shared/models/preferences/gradebook-preferences.model';
import {GradingPeriod} from './models/grading-period.model';
import {Category} from '../shared/models/category.model';
import {Score} from './models/score.model';
import {OverrideCode} from './models/override-code.model';
import {GradingScale} from '../shared/models/grading-scale.model';
import {Gradebook} from './models/gradebook.model';
import {ExtraCredit} from './models/extra-credit.model';
import {StandardObjects} from './models/standard-objects.model';
import {StandardScore} from './models/standard-score.model';
import {StandardScale} from './models/standard-scale.model';
import {BehaviorObjects} from './models/behavior-objects.model';
import {BehaviorScale} from './models/behavior-scale.model';
import {BehaviorMark} from './models/behavior-mark.model';
import {CourseWorkStandardScore} from './models/course-work-standard-score.model';
import {CourseWorkStandardObjects} from './models/course-work-standard-objects.model';
import {Standard} from './models/standard.model';
import {GradebookCommentObject} from '../shared/models/gradebook-comment-objects.model';
import {GradebookComment} from '../shared/models/gradebook-comment.model';
import {AutoUnsubscribe} from 'ngx-auto-unsubscribe';
import {GradePostingHistory} from './models/grade-posting-history.model';
import {Attachment} from '../../../shared-components/models/attachment.model';
import {GradebookBackup} from './models/gradebook-backup.model';
import {CategorySummary} from './models/category-summary.model';
import {RetakeScore} from './modals/retake-scores/retake-score.model';
import {GBAdminPermission} from './models/gb-admin-permission.model';
import {GradebookIndicator} from '../shared/models/gradebook-indicator.model';
import {UserPreferencesService} from '../../../shared-components/user-preferences/user-preferences.service';
import {StudentPicture} from './models/student-picture.model';
import {SharedService} from '../../../shared-components/shared.service';
import {SharedGradebookService} from '../shared/shared-gradebook.service';
import { GradingTypeV10 } from './models/grading-type-v10.model';
import { GradebookV10StandardsService } from './gradebook-v10-standards.service';

@AutoUnsubscribe()
@Injectable()
export class GradebookV10Service implements OnDestroy {

    public course: ClassroomSummary;
    gradebook: Gradebook;
    defaultYear: Year;
    assignments: V10Assignment[];
    students: Student[];
    overrideCodes: OverrideCode[];
    gradingScale: GradingScale[];
    extraCredit: ExtraCredit[];
    postingHistory: GradePostingHistory[];
    showOnlyCW_ID: number;
    showSingleStudent = false;
    allStudentStandardsShown = false;
    adminPermissions: GBAdminPermission[];
    attendanceDate: Date;
    showAlertsAndAttendance: boolean;
    initPreferences: boolean;
    stickAddDetailedAssignment: boolean;
    studentViewSelection: Student;
    selectedStandardStudent: Student;

    private _unsavedChanges = false;
    get unsavedChanges(): boolean {
        return this._unsavedChanges;
    }

    set unsavedChanges(value: boolean) {
        if (value !== this._unsavedChanges) {
            this._unsavedChanges = value;
            this.startAutosave();
        }
    }

    selectedTab: string;
    selectedStandardTab: string;
    selectedReportingTab: string;
    selectedReportingStandardTab: string;

    gradebookPreferences: GradebookPreferences;
    observablePreferences = new Subject<GradebookPreferences>();
    observablePreferencesStream$ = this.observablePreferences.asObservable();

    calculationType: number;

    selectedGradingPeriod: GradingPeriod;
    gradingPeriods: GradingPeriod[];
    observableGradingPeriods = new Subject<GradingPeriod[]>();
    observableGradingPeriodStream$ = this.observableGradingPeriods.asObservable();

    selectedCategory: Category;
    categories: Category[];
    observableCategories = new Subject<Category[]>();
    observableCategoryStream$ = this.observableCategories.asObservable();

    observableGradebook = new Subject<any>();
    observableGradebookStream$ = this.observableGradebook.asObservable();

    observableAssignments = new Subject<V10Assignment[]>();
    observableAssignmentStream$ = this.observableAssignments.asObservable();
    updatedAssignments: number[] = [];

    observableAssignmentFilter = new Subject<any>();
    observableAssignmentFilterStream$ = this.observableAssignmentFilter.asObservable();

    observablePermissions = new Subject<GBAdminPermission[]>();
    observablePermissionStream$ = this.observablePermissions.asObservable();

    observableAutosaveStart = new Subject<GBAdminPermission[]>();
    observableAutosaveStartStream$ = this.observableAutosaveStart.asObservable();

    standardObjects: StandardObjects;
    courseWorkStandardObjects: CourseWorkStandardObjects;
    courseWorkStandardScores: CourseWorkStandardScore[];
    standards: Standard[];
    standardScales: StandardScale[];
    behaviorScales: BehaviorScale[];

    indicators: GradebookIndicator[];
    showIndicators: boolean;

    showMassAdd: boolean;
    showNonScoredStandards = true;
    showStandardAnalysisTrends = false;

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private userPreferencesService: UserPreferencesService,
        private sharedService: SharedService,
        private sharedGradebookService: SharedGradebookService,
        private standardService: GradebookV10StandardsService
    ) {
        this.defaultYear = this.yearService.defaultYear;
        this.selectedTab = 'Scores';
        this.selectedStandardTab = 'Standards for One Assignment';
        this.selectedReportingTab = 'Traditional';
        this.selectedReportingStandardTab = 'All Standards by Student';
    }

    startAutosave() {
        this.observableAutosaveStart.next();
    }

    updateGradebook(gradebookData) {
        this.gradebook = gradebookData.gradebook;
        this.students = gradebookData.gradebook.students;
        if (this.gradebookPreferences.StudentSortOrder == 'StudentName') {
            this.students = this.sortStudents(this.students);
        }
        for (let i = 0; i < this.students.length; i++) {
            this.students[i].index = i;
        }
        this.assignments = gradebookData.gradebook.assignments;
        for (let i = 0; i < this.assignments.length; i++) {
            this.assignments[i].index = i;
        }
        this.overrideCodes = gradebookData.codesScalesCredit.OverrideCodes;
        this.gradingScale = gradebookData.codesScalesCredit.GradingScale;
        this.extraCredit = gradebookData.codesScalesCredit.ExtraCredit;
        this.postingHistory = gradebookData.gradebook.GradePostingHistory;

        if (this.course.ScoreStandardsByAssignment) {
            this.standardObjects = gradebookData.standardBehaviorObjects.StandardObjects;
            this.standards = this.standardObjects.Standards;
            this.standardScales = this.standardObjects.StandardScales;
            this.courseWorkStandardObjects = gradebookData.courseWorkStandardObjects;
            this.courseWorkStandardScores = this.courseWorkStandardObjects.CourseWorkStandardScores;
        }

        this.observableGradebook.next(gradebookData);
    }

    sortStudents(students: Student[]) {
        switch (this.userPreferencesService.generalPreferences.StudentNameFormat.charAt(0)) {
            case 'F':
                students.sort((a: Student, b: Student) => {
                    const firstNameComparison = a.FirstName.replace('\'', '').localeCompare(b.FirstName.replace('\'', ''));
                    if (firstNameComparison == 0) {
                        return a.LastName.replace('\'', '').localeCompare(b.LastName.replace('\'', ''));
                    } else {
                        return firstNameComparison;
                    }
                });
                break;
            case 'L':
                students.sort((a: Student, b: Student) => {
                    const lastNameComparison = a.LastName.replace('\'', '').localeCompare(b.LastName.replace('\'', ''));
                    if (lastNameComparison == 0) {
                        return a.FirstName.replace('\'', '').localeCompare(b.FirstName.replace('\'', ''));
                    } else {
                        return lastNameComparison;
                    }
                });
                break;
            default:
                break;
        }
        return students;
    }

    updateGradingPeriods(periods: GradingPeriod[]) {
        let index = 0;
        for (const period of periods.filter(reportingPeriod => reportingPeriod.ReportingTerm)) {
            period.reportingIndex = index;
            period.StartDate = new Date(period.StartDate);
            period.EndDate = new Date(period.EndDate);
            index = index + 1;
        }
        index = 0;
        for (const period of periods.filter(standardPeriod => standardPeriod.ReportingTerm && standardPeriod.ForStandards)) {
            period.standardIndex = index;
        }
        periods.push(this.addFinalPeriod(index));
        this.gradingPeriods = periods;
        this.observableGradingPeriods.next(periods);
    }

    updateCategories(categories) {
        this.categories = categories;
        this.observableCategories.next(categories);
    }

    updateAssignments(assignments) {
        this.assignments = assignments;
        this.observableAssignments.next(assignments);
    }

    updateAssignmentFilter() {
        this.observableAssignmentFilter.next();
    }

    updatePreferences(preferences) {
        this.gradebookPreferences = JSON.parse(preferences);
        this.calculationType = parseInt(this.gradebookPreferences.FGradeCalculationType);
        this.observablePreferences.next(this.gradebookPreferences);
        this.showAlertsAndAttendance = this.gradebookPreferences.NonScrollingColumns.filter(c => c.Field == 'Alerts' || c.Field == 'AttendanceInformation').length > 0 || this.gradebookPreferences.ScrollingColumns.filter(c => c.Field == 'Alerts' || c.Field == 'AttendanceInformation').length > 0;
    }


    loadPeriodsCategoriesPreferences(): Observable<any> {
        return observableForkJoin(
            this.http.get(System.apiURL.Classroom + 'GetGradebookTermsBySiteSectionId?siteSectionId=' + this.course.SSEC_ID + '&tgsId=' + this.course.Id + '&showTermOnly=false&yearId=' + this.defaultYear.Id + '&schoolId=' + this.defaultYear.SchoolID),
            this.sharedService.getCategories(true, this.course.Id, ''),
            this.http.get(System.apiURL.Student + 'GetUserPreferences?studentId=0&pageURL=/OneSISGradeBook&subjectListId=' + this.course.Id + '&subjectSection=\'\'&isDefaultPreferences=false'),
            (gradingPeriods, categories, preferences) => ({gradingPeriods, categories, preferences})
        );
    }

    getGradebook(): Observable<any> {
        if (!this.showAlertsAndAttendance) {
            return observableForkJoin(
                this.http.get(System.apiURL.Classroom + 'GetGradebookV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradebookScoresV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetOverridesScalesExtraCredit?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetStandardBehaviorObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&dataBucketId=' + this.course.DataBucket_ID),
                (gradebook, scores, reportCardGrades, codesScalesCredit, standardBehaviorObjects) => ({
                    gradebook,
                    scores,
                    reportCardGrades,
                    codesScalesCredit,
                    standardBehaviorObjects
                })
            );
        } else {
            return observableForkJoin(
                this.http.get(System.apiURL.Classroom + 'GetGradebookV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradebookScoresV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetOverridesScalesExtraCredit?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetStandardBehaviorObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&dataBucketId=' + this.course.DataBucket_ID),
                this.http.get(System.apiURL.Classroom + 'GetGradebookAlertsAndAttendance?tgsId=' + this.course.Id + '&yearId=' + this.yearService.defaultYear.Id + '&schoolId=' + this.yearService.defaultYear.SchoolID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                (gradebook, scores, reportCardGrades, codesScalesCredit, standardBehaviorObjects, alertsAttendance) => ({
                    gradebook,
                    scores,
                    reportCardGrades,
                    codesScalesCredit,
                    standardBehaviorObjects,
                    alertsAttendance
                })
            );
        }
    }

    getGradebookWithAssignmentStandards(): Observable<any> {
        if (!this.showAlertsAndAttendance || (this.course.ScoreStandardsByAssignment && !this.course.HybridGradebook)) {
            return observableForkJoin(
                this.http.get(System.apiURL.Classroom + 'GetGradebookV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradebookScoresV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetOverridesScalesExtraCredit?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetStandardBehaviorObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&dataBucketId=' + this.course.DataBucket_ID),
                this.http.get(System.apiURL.Classroom + 'GetCourseWorkStandardObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                (gradebook, scores, reportCardGrades, codesScalesCredit, standardBehaviorObjects, courseWorkStandardObjects) => ({
                    gradebook,
                    scores,
                    reportCardGrades,
                    codesScalesCredit,
                    standardBehaviorObjects,
                    courseWorkStandardObjects
                })
            );
        } else {
            return observableForkJoin(
                this.http.get(System.apiURL.Classroom + 'GetGradebookV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradebookScoresV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetOverridesScalesExtraCredit?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetStandardBehaviorObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&dataBucketId=' + this.course.DataBucket_ID),
                this.http.get(System.apiURL.Classroom + 'GetCourseWorkStandardObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                this.http.get(System.apiURL.Classroom + 'GetGradebookAlertsAndAttendance?tgsId=' + this.course.Id + '&yearId=' + this.yearService.defaultYear.Id + '&schoolId=' + this.yearService.defaultYear.SchoolID + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
                (gradebook, scores, reportCardGrades, codesScalesCredit, standardBehaviorObjects, courseWorkStandardObjects, alertsAttendance) => ({
                    gradebook,
                    scores,
                    reportCardGrades,
                    codesScalesCredit,
                    standardBehaviorObjects,
                    courseWorkStandardObjects,
                    alertsAttendance
                })
            );
        }
    }

    getGradingPeriods(course: ClassroomSummary, yearId: number, schoolId: number): Observable<GradingPeriod[]> {
        return this.http.get<GradingPeriod[]>(System.apiURL.Classroom + 'GetGradebookTermsBySiteSectionId?siteSectionId=' + course.SSEC_ID + '&tgsId=' + course.Id + '&showTermOnly=false&yearId=' + yearId + '&schoolId=' + schoolId);
    }

    getCategories(): Observable<Category[]> {
        return this.sharedService.getCategories(true, this.course.Id, '');
    }

    getAssignments(): Observable<V10Assignment[]> {
        return this.http.get<V10Assignment[]>(System.apiURL.Classroom + 'GetGradebookAssignmentsV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }

    getCategoriesAndAssignments(courseId: number, periodId: number) {
        return observableForkJoin(
            this.sharedService.getCategories(true, courseId, ''),
            this.http.get<V10Assignment[]>(System.apiURL.Classroom + 'GetGradebookAssignmentsV10?tgsId=' + courseId + '&gradingPeriod=' + periodId),
            (categories, assignments) => ({categories, assignments})
        );
    }

    getCourseWorkScores(): Observable<Score[]> {
        return this.http.get<Score[]>(System.apiURL.Classroom + 'GetGradebookScoresV10?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }

    getGradeReporting(): Observable<Score[]> {
        return this.http.get<Score[]>(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }

    getStandards(): Observable<StandardObjects> {
        return this.http.get<StandardObjects>(System.apiURL.Classroom + 'GetGBStandards?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }

    getBehaviorObjects(): Observable<BehaviorObjects> {
        return this.http.get<BehaviorObjects>(System.apiURL.Classroom + 'GetBehaviorMarks?courseId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID);
    }

    getStandardsByAssignment(): Observable<any> {
        return observableForkJoin(
            this.http.get(System.apiURL.Classroom + 'GetCourseWorkStandardObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
            this.http.get(System.apiURL.Classroom + 'GetGBStandards?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id),
            (courseWorkStandardObjects, standardObjects) => ({courseWorkStandardObjects, standardObjects})
        );
    }

    getCourseWorkStandardObjects(): Observable<CourseWorkStandardObjects> {
        return this.http.get<CourseWorkStandardObjects>(System.apiURL.Classroom + 'GetCourseWorkStandardObjects?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }

    getGradebookComments(courseId: number, forAssignments: boolean) {
        const parameters = '?courseId=' + courseId + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&forAssignments=' + forAssignments;
        return this.http.get<GradebookCommentObject>(System.apiURL.Classroom + 'GetGradebookCommentsV10' + parameters);
    }

    getGradebookAttachments(cwId: number): Observable<Attachment[]> {
        return this.http.get<Attachment[]>(System.apiURL.Classroom + 'GetGradebookAttachmentsV10?cwId=' + cwId);
    }

    getBackupRecords() {
        return this.http.get<GradebookBackup[]>(System.apiURL.Classroom + 'GetV10GradebookBackups?tgsId=' + this.course.Id);
    }

    getCategorySummary(studentId: number, gradingPeriod: number) {
        return this.http.get<CategorySummary[]>(System.apiURL.Classroom + 'GetCategorySummary?studentId=' + studentId + '&courseId=' + this.course.Id + '&gradingPeriod=' + gradingPeriod);
    }

    getCopyScoresInfo(course: ClassroomSummary, periodId: number) {
        return observableForkJoin(
            this.http.get<Score[]>(System.apiURL.Classroom + 'GetGradeReportingV10?tgsId=' + course.Id + '&dataBucketId=' + course.DataBucket_ID + '&gradingPeriod=' + periodId),
            this.http.get<Score[]>(System.apiURL.Student + 'GetStudentFinalGrades?tgsId=' + course.Id + '&gradingPeriod=' + periodId),
            this.http.get(System.apiURL.Classroom + 'GetOverridesScalesExtraCredit?tgsId=' + course.Id + '&gradingPeriod=' + periodId),
            (reportingGrades, finalGrades, scalesOverrides) => ({reportingGrades, finalGrades, scalesOverrides})
        );
    }

    copyScores(sourceCourse: number, studentId: number, gradingPeriod: number, copyOption: number, overrideCode: string, scores: Score[]) {
        return this.http.post(System.apiURL.Classroom + 'CopyStudentScores?sourceSSEC=' + sourceCourse + '&destSSEC=' + this.course.SSEC_ID + '&studentId=' + studentId + '&gradingPeriod=' + gradingPeriod + '&assignmentOption=' + copyOption + '&gradeOverride=' + overrideCode, scores);
    }

    getAssignmentRetakes(cwId: number) {
        return this.http.get<RetakeScore[]>(System.apiURL.Classroom + 'GetAssignmentRetakeScores?cwId=' + cwId);
    }

    getAdminPermissions() {
        return this.http.get<GBAdminPermission[]>(System.apiURL.Classroom + 'GetGradebookAdminPermissions?yearId=' + this.yearService.defaultYear.Id + '&schoolId=' + this.yearService.defaultYear.SchoolID);
    }

    getIndicators(courseId: number, periodId: number): Observable<any> {
        return this.sharedGradebookService.getGradebookIndicators(courseId, periodId);
    }

    getAssignmentsForAllPeriods() {
        return this.http.get<any>(System.apiURL.Classroom + 'GetAssignmentsForAllGradingPeriods?tgsId=' + this.course.Id);
    }

    getAlertsAndAttendance() {
        return this.http.get<any>(System.apiURL.Classroom + 'GetGradebookAlertsAndAttendance?tgsId=' + this.course.Id + '&yearId=' + this.yearService.defaultYear.Id + '&schoolId=' + this.yearService.defaultYear.SchoolID + '&gradingPeriod=' + this.selectedGradingPeriod.Id);
    }


    saveAssignments(courseId: number, gradingPeriod: number, scoreStandardsByAssignment: boolean, assignments: V10Assignment[]): Observable<string> {
        const parameters = '?courseId=' + courseId + '&yearId=' + this.yearService.defaultYear.Id + '&gradingperiod=' + gradingPeriod + '&scoreStandardsByAssignment=' + scoreStandardsByAssignment;
        return this.http.post<string>(System.apiURL.Classroom + 'SaveGradebookAssignmentV10' + parameters, assignments);
    }

    saveCourseWorkScores(scores: Score[], hybridGradebook: boolean): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveCourseWorkScores?tgsId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&hybrid=' + hybridGradebook, scores);
    }

    saveFinalGrades(courseId: number, periodId: number, students: Student[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveFinalGrades?tgsId=' + courseId + '&gradingPeriod=' + periodId, students);
    }

    postGrades(postingPeriod: number): Observable<void> {
        return this.http.get<void>(System.apiURL.Classroom + 'PostV10Grades?gradingPeriod=' + this.selectedGradingPeriod.Id + '&postingPeriod=' + postingPeriod + '&tgsId=' + this.course.Id + '&cwId=-1');
    }

    postAssignmentStandardScores(students: Student[], sourcePeriod: GradingPeriod, destinationPeriod: GradingPeriod): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'PostAssignmentStandardScores?courseId=' + this.course.Id + '&sourcePeriod=' + sourcePeriod.Id + '&destinationPeriod=' + destinationPeriod.Id, students);
    }

    saveReportCardGrades(scores: Score[], finalize: boolean): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveGradeReportingV10?tgsId=' + this.course.Id + '&dataBucketId=' + this.course.DataBucket_ID + '&gradingPeriod=' + this.selectedGradingPeriod.Id + '&finalize=' + finalize, scores);
    }

    saveStandardScores(scores: StandardScore[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveGBStandards?courseId=' + this.course.Id, scores);
    }

    saveBehaviorMarks(marks: BehaviorMark[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveBehaviorMarks?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id, marks);
    }

    finalizeGradeReporting(finalize: boolean, periodIds: number[]): Observable<GradePostingHistory[]> {
        return this.http.post<GradePostingHistory[]>(System.apiURL.Classroom + 'FinalizeGradeReporting?tgsId=' + this.course.Id + '&finalize=' + finalize, periodIds);
    }

    saveAssignmentStandardScores(scores: CourseWorkStandardScore[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveAssignmentStandardScores', scores);
    }

    saveAssignmentComments(scores: Score[], cwId: number): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveAssignmentCommentsV10?courseId=' + this.course.Id + '&termId=' + this.selectedGradingPeriod.Id + '&cwId=' + cwId, scores);
    }

    saveReportCardComments(comments: GradebookComment[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveReportCardCommentsV10?courseId=' + this.course.Id, comments);
    }

    saveStandardComments(comments: GradebookComment[]): Observable<void> {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveStandardComments?courseId=' + this.course.Id, comments);
    }

    deleteCategory(categoryId: number) {
        return this.http.get<boolean>(System.apiURL.Classroom + 'DeleteGradebookCategoryV10?categoryId=' + categoryId);
    }

    deleteAssignment(courseId: number, cwId: number) {
        return this.http.get<boolean>(System.apiURL.Classroom + 'DeleteGradebookAssignmentV10?courseId=' + courseId + '&cwId=' + cwId);
    }

    createBackup(backupName: string, backupType: string) {
        return this.http.get(System.apiURL.Classroom + 'GradebookCreateBackupV10?tgsId=' + this.course.Id + '&name=' + backupName + '&saveType=' + backupType);
    }

    restoreBackup(restoreType: string, backupId: number, backup) {
        return this.http.post(System.apiURL.Classroom + 'GradebookRestoreBackupV10?restoreType=' + restoreType + '&backupId=' + backupId, backup);
    }

    deleteBackup(backupId) {
        return this.http.get(System.apiURL.Classroom + 'DeleteBackupSet?BackupId=' + backupId);
    }

    copyAssignments(courseList: string, verifyNewCategory: boolean, assignments: V10Assignment[]) {
        return this.http.post<string>(System.apiURL.Classroom + 'GradebookAssignmentCopyV10?courseList=' + courseList + '&verifyNewCategory=' + verifyNewCategory, assignments);
    }

    copyCategories(categoryList: string, gradingPeriods: string, validateWeight: boolean) {
        return this.http.get<string>(System.apiURL.Classroom + 'CopyCategories?tgsId=' + this.course.Id + '&categoryList=' + categoryList + '&gradingPeriods=' + gradingPeriods + '&validateWeight=' + validateWeight);
    }

    copyIndicators(sourceCourse: number, courses: string, indicators: GradebookIndicator[]) {
        return this.http.post<string>(System.apiURL.Classroom + 'CopyGradebookIndicators?sourceCourse=' + sourceCourse + '&courses=' + courses, indicators);
    }

    saveStudentNotes(studentId: number, periodId: number, notes: string) {
        return this.http.get(System.apiURL.Classroom + 'SaveStudentNotes?ssecId=' + this.course.SSEC_ID + '&studentId=' + studentId + '&gradingPeriod=' + periodId + '&notes=' + notes);
    }

    saveAssignmentRetakes(cwId: number, retakeOption: number, retakes: RetakeScore[]) {
        return this.http.post(System.apiURL.Classroom + 'SaveAssignmentRetakeScores?cwId=' + cwId + '&postOption=' + retakeOption, retakes);
    }

    saveIndicator(indicator: GradebookIndicator): Observable<any> {
        return this.sharedGradebookService.saveGradebookIndicator(indicator, this.course.Id);
    }

    saveExtraCredit(extraCredit: ExtraCredit[]): Observable<ExtraCredit[]> {
        return this.http.post<ExtraCredit[]>(System.apiURL.Classroom + 'SaveGBExtraCredit?courseId=' + this.course.Id + '&gradingPeriod=' + this.selectedGradingPeriod.Id, extraCredit);
    }

    deleteIndicator(indicatorId: number) {
        return this.sharedGradebookService.deleteGradebookIndicator(indicatorId);
    }

    createPreferenceRecords(courseId: number) {
        return this.http.get(System.apiURL.Classroom + 'CreateGBPReferenceRecords?tgsId=' + courseId);
    }

    getStudentPicturesByIds(studentIds: number[]): Observable<StudentPicture[]> {
        return this.http.post<StudentPicture[]>(System.apiURL.Classroom + 'GetStudentPicturesByIds', studentIds);
    }

    setCustomStudentSort(studentIds: number[]) {
        return this.http.post<void>(System.apiURL.Classroom + 'SetCustomStudentOrder?tgsId=' + this.course.Id, studentIds);
    }

    saveGradebookNumbers(students: Student[]) {
        return this.http.post<void>(System.apiURL.Classroom + 'SaveGradebookNumbers?ssecId=' + this.course.SSEC_ID, students);
    }

    getGradingTypeV10() {
        return this.http.get<GradingTypeV10>(System.apiURL.Classroom + 'GetGradingTypeV10?ssecId=' + this.course.SSEC_ID);
    }


    addFinalPeriod(index): GradingPeriod {
        const finalPeriod = new GradingPeriod();
        finalPeriod.Id = -1;
        finalPeriod.Description = 'Final Score';
        finalPeriod.IsLocked = false;
        finalPeriod.IsCalculated = true;
        finalPeriod.ReportingTerm = true;
        finalPeriod.reportingIndex = index;
        return finalPeriod;
    }

    clearData(changeCourse: boolean) {
        this.course = undefined;
        this.gradebook = undefined;
        this.assignments = undefined;
        this.students = undefined;
        this.gradingScale = undefined;
        this.extraCredit = undefined;
        this.gradebookPreferences = undefined;
        this.calculationType = undefined;
        this.selectedGradingPeriod = undefined;
        this.selectedCategory = undefined;
        this.gradingPeriods = undefined;
        this.categories = undefined;
        this.standards = undefined;
        this.indicators = undefined;
        this.showOnlyCW_ID = undefined;
        this.showSingleStudent = false;
        this.showIndicators = undefined;
        this.standardService.StandardsHierarchy = undefined;
        if (!changeCourse) {
            this.selectedReportingTab = 'Traditional';
            this.selectedReportingStandardTab = 'All Standards by Student';
        }
    }

    exportToExcel(periodColumns: GradingPeriod[]) {
        let html = '';
        const uri = 'data:application/vnd.ms-excel;base64,';
        const template = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--><meta http-equiv="content-type" content="text/plain; charset=UTF-8" /></head><body><table>{table}</table></body></html>';
        const base64 = function (s) {
            return window.btoa(unescape(encodeURIComponent(s)));
        };

        const format = function (s, c) {
            return s.replace(/{(\w+)}/g, function (m, p) {
                return c[p];
            });
        };

        html = '<thead><tr><th style="vertical-align:bottom">Student Name</th>';

        // Student Number
        if (this.gradebookPreferences.NonScrollingColumns.filter(nsc => nsc.DisplayName == 'Student #').length > 0) {
            html = html + '<th style="vertical-align:bottom">Student #</th>';
        }

        // Grading Period Columns
        for (let i = 0; i < periodColumns.length; i++) {
            html = html + '<th style="vertical-align:bottom">';
            html = html + periodColumns[i].Description + '<br style="mso-data-placement:same-cell;">';
            html = html + periodColumns[i].HeaderDate;
            html = html + '</th>';
        }

        let assignmentDecimalFormat = '';
        if (parseInt(this.gradebookPreferences.NumericalGradeRoundTo) == 1) {
            assignmentDecimalFormat = 'style=\'mso-number-format:"#,#0.0"\'';
        } else if (parseInt(this.gradebookPreferences.NumericalGradeRoundTo) == 2) {
            assignmentDecimalFormat = 'style=\'mso-number-format:"#,##0.00"\'';
        }
        let gpCWDecimalFormat = '';
        if (parseInt(this.gradebookPreferences.GPCWRoundToValue) == 1) {
            gpCWDecimalFormat = 'style=\'mso-number-format:"#,#0.0"\'';
        } else if (parseInt(this.gradebookPreferences.GPCWRoundToValue) == 2) {
            gpCWDecimalFormat = 'style=\'mso-number-format:"#,##0.00"\'';
        }

        // Assignment Columns
        for (let i = 0; i < this.assignments.length; i++) {
            html = html + '<th style="vertical-align:bottom">';
            const assignment = this.assignments[i];
            const dueDate = (assignment.DueDate) ? new Date(assignment.DueDate) : undefined;
            const dateAssigned = new Date(assignment.AssignedDate);
            const assignmentHeadings = this.gradebookPreferences.AssignmentHeading.filter(heading => heading.value);
            for (let j = 0; j < assignmentHeadings.length; j++) {
                if (j > 0) {
                    html = html + '<br style="mso-data-placement:same-cell;">';
                }
                const headingPreference = assignmentHeadings[j];
                if (headingPreference.value) {
                    if (headingPreference.Field == 'ShortName') {
                        html = html + assignment.ShortName;
                    }
                    if (headingPreference.Field == 'PossiblePoints') {
                        html = html + assignment.PossiblePoints + ' Pts';
                    } else if (headingPreference.Field == 'DateAssigned') {
                        html = html + 'A ' + ('0' + (dateAssigned.getMonth() + 1)).slice(-2) + '/' + ('0' + dateAssigned.getDate()).slice(-2);
                    } else if (dueDate && headingPreference.Field == 'DateDue') {
                        html = html + 'D ' + ('0' + (dueDate.getMonth() + 1)).slice(-2) + '/' + ('0' + dueDate.getDate()).slice(-2);
                    } else if (headingPreference.Field == 'MeanMedianRange') {
                        html = html + 'M ' + assignment.Mean + '/Md ' + assignment.Median + '/R ' + assignment.Range;
                    } else if (headingPreference.Field == 'Summary') {
                        if (this.gradebookPreferences.SummaryCharacter) {
                            const summary = assignment.Summary.substring(0, this.gradebookPreferences.SummaryCharacterCount);
                            html = (assignment.Summary.length > this.gradebookPreferences.SummaryCharacterCount) ? html + summary + '...' : html + summary;
                        } else {
                            const summary = assignment.Summary;
                            html = html + summary;
                        }
                    }
                }
            }
            html = html + '</th>';
        }
        html = html + '</tr></thead><tbody>';
        for (let i = 0; i < this.students.length; i++) {
            html = html + '<tr><td>' + this.students[i].FirstName + ' ' + this.students[i].LastName + '</td>';

            // Student Number
            if (this.gradebookPreferences.NonScrollingColumns.filter(nsc => nsc.DisplayName == 'Student #').length > 0) {
                html = html + '<td>' + this.students[i].StudentNumber + '</td>';
            }

            for (let j = 0; j < periodColumns.length; j++) {
                if (periodColumns[j].Id == this.selectedGradingPeriod.Id) {
                    html = html + '<td ' + gpCWDecimalFormat + '>' + this.students[i].Final + '</td>';
                } else {
                    const score = this.students.filter(student => student.Id == this.students[i].Id)[0].ReportCardGrades.filter(score => score.DBSC_ID == periodColumns[j].Id)[0];
                    html = html + '<td ' + gpCWDecimalFormat + '>' + score.Score + '</td>';
                }
            }
            for (let j = 0; j < this.assignments.length; j++) {
                const score = this.students[i].scores.filter(score => score.CW_ID == this.assignments[j].CW_ID)[0];
                if (score != undefined) {
                    html = html + '<td ' + assignmentDecimalFormat + '>' + score.Score + '</td>';
                } else {
                    html = html + '<td></td>';
                }
            }
            html = html + '</tr>';
        }
        html = html + '</tbody>';

        const ctx = {
            worksheet: 'Worksheet',
            table: html
        };
        const link = document.createElement('a');
        const date = (new Date().getMonth() + 1) + '.' + new Date().getDate() + '.' + new Date().getFullYear();
        link.download = 'gradebookExport[' + date + '].xls';
        link.href = uri + base64(format(template, ctx));
        link.click();
    }

    ngOnDestroy() {
    }

}
